/**
 * TOSS Web Application - Provider Components
 *
 * Root provider wrapper that initializes and provides:
 * - TanStack Query (server state management)
 * - React Query DevTools (development only)
 * - Auth event listener setup
 * - Future providers (Router, Theme, etc.)
 */

import { ReactNode, useEffect, useRef, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient, isDevToolsEnabled, clearQueryCache } from './queryClient';
import {
  useAuthStore,
  setupAuthEventListener,
  cleanupAuthEventListener,
  useToast,
} from '@/store';
import { useRealtimeNotifications } from '@/realtime/useNotifications';
import { AppBackgroundProvider } from '@/theme/AppBackgroundContext';

// ============================================================================
// Types
// ============================================================================

/**
 * Props for the main AppProviders component
 */
interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Props for the QueryProvider component
 */
interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Props for the AuthInitializer component
 */
interface AuthInitializerProps {
  children: ReactNode;
}

// ============================================================================
// Service-worker cache purge on logout
// ============================================================================

/**
 * Ask the active service worker to drop its runtime caches (`src/sw.ts` handles the
 * message). Called whenever the session ends — the browser cache is the one place a
 * previous account's bytes can outlive `clearQueryCache()`.
 *
 * Authenticated API responses are no longer cached at all (that route was removed in
 * `src/sw.ts`), and images under `/api/*` are excluded, so in a correctly-behaving
 * browser there is nothing user-specific left to delete. The purge is what turns that
 * from a claim about our route predicates into a fact — including for a browser still
 * running the *previous* generation of the worker, which does hold an `api-cache`.
 *
 * Fully guarded: no `serviceWorker` (older Safari, insecure origin), no registration
 * (dev without the PWA plugin), or an uncontrolled first load — every one of those is a
 * silent no-op, never a broken logout.
 */
function purgeServiceWorkerCaches(): void {
  const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
  if (!sw) return;

  const message = { type: 'TOSS_PURGE_RUNTIME_CACHES' };

  if (sw.controller) {
    sw.controller.postMessage(message);
    return;
  }

  // Page not controlled yet (first load right after install, hard reload). Resolve the
  // registration instead — `getRegistration()` settles with `undefined` when there is
  // none, unlike `ready`, which would hang forever in a dev build without a worker.
  sw.getRegistration()
    .then((registration) => registration?.active?.postMessage(message))
    .catch(() => {
      /* logout must never fail because of cache cleanup */
    });
}

// ============================================================================
// Query Provider
// ============================================================================

/**
 * TanStack Query provider with DevTools
 *
 * Wraps children with QueryClientProvider and optionally
 * renders ReactQueryDevtools in development mode.
 *
 * @example
 * ```tsx
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 * ```
 */
function QueryProvider({ children }: QueryProviderProps): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {isDevToolsEnabled && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom"
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
}

// ============================================================================
// Auth Initializer
// ============================================================================

/**
 * Component that handles auth initialization on app startup
 *
 * - Initializes auth state from stored tokens
 * - Sets up auth event listeners for auto-logout
 * - Shows loading state while initializing
 * - Clears query cache on logout
 *
 * @example
 * ```tsx
 * <AuthInitializer>
 *   <App />
 * </AuthInitializer>
 * ```
 */
function AuthInitializer({ children }: AuthInitializerProps): JSX.Element {
  const [isReady, setIsReady] = useState(false);
  const initialize = useAuthStore((state) => state.initialize);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const { warning: showWarning } = useToast();

  // Initialize real-time notifications (auto-connects when authenticated). A toast only for the
  // priority sector (24.09.2026): the other events are the feed's business, and a desk that gets a
  // popup for every «ПТО взята в работу» learns to ignore the one about a client's application.
  useRealtimeNotifications({
    onNotification: (notification) => {
      if (notification.isPriority) {
        showWarning(notification.title, notification.content);
      }
    },
  });

  // Note: Firebase messaging SW is now registered lazily inside
  // `getMessagingToken()` (see `src/lib/firebase.ts`). Registering up-front
  // causes a race condition on Android PWA where `getToken()` runs before
  // the SW reaches `activated` state.

  // Initialize auth state on mount
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        await initialize();
      } finally {
        if (isMounted) {
          setIsReady(true);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, [initialize]);

  // A session held back by a dead network retries itself the moment the network is back — the
  // customer should not have to guess that the way out of "нет связи" is to reload the app.
  useEffect(() => {
    const retryWhenOnline = () => {
      if (useAuthStore.getState().isOfflineBlocked) {
        void initialize();
      }
    };

    window.addEventListener('online', retryWhenOnline);
    return () => window.removeEventListener('online', retryWhenOnline);
  }, [initialize]);

  // Setup auth event listeners
  useEffect(() => {
    setupAuthEventListener();

    // Listen for logout events to clear query cache + the service worker's runtime caches
    const handleLogout = () => {
      clearQueryCache();
      purgeServiceWorkerCaches();
    };

    window.addEventListener('auth:logout', handleLogout);

    return () => {
      cleanupAuthEventListener();
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  // Clear cache when authentication state changes to false
  // (user logged out or session expired)
  const wasAuthenticatedRef = useRef(false);
  useEffect(() => {
    if (!isReady) return;

    if (isAuthenticated) {
      wasAuthenticatedRef.current = true;
      return;
    }

    // Clear query cache to remove any cached user-specific data
    // This ensures a clean state for the next user
    clearQueryCache();

    // Purge the service worker's runtime caches on the same transition — but only on a
    // real authenticated → signed-out one, never on a plain anonymous page load, which
    // would drop `static-resources` and re-download every JS/CSS chunk on each visit to
    // the login screen.
    //
    // This is the branch that catches a *user-initiated* logout: `authStore.logout()`
    // (the sidebar's «Выйти» via `logoutWithPushCleanup`, and `useLogout`) only mutates
    // the store, while the `auth:logout` window event handled above is dispatched
    // exclusively by the API interceptors on a 401 / failed refresh. Handling only that
    // event would leave the common case unpurged.
    if (wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = false;
      purgeServiceWorkerCaches();
    }
  }, [isReady, isAuthenticated]);

  // Show loading state while initializing
  if (!isReady) {
    return <AppLoadingScreen />;
  }

  return <>{children}</>;
}

// ============================================================================
// Loading Screen
// ============================================================================

/**
 * Full-page branded splash screen shown during app initialization
 *
 * Displays while:
 * - Auth state is being restored from stored tokens
 * - Initial data is being fetched
 *
 * Design spec (from Flutter TOSS):
 * - Background: #3F4CBF (Accent Blue)
 * - Logo: Logo2.png centered
 * - Width: 20% on desktop (min 150px, max 300px), 60% on mobile
 */
function AppLoadingScreen(): JSX.Element {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[var(--color-primary-500)]">
      <img
        src="/assets/logo-square.png"
        alt="TOSS"
        className="w-[60%] min-w-[150px] max-w-[300px] md:w-[20%]"
      />
    </div>
  );
}

// ============================================================================
// Main App Providers
// ============================================================================

/**
 * Root provider wrapper for the TOSS application
 *
 * Composes all necessary providers in the correct order:
 * 1. QueryProvider - Server state management (TanStack Query)
 * 2. AuthInitializer - Auth state initialization
 * 3. (Future) RouterProvider - Routing
 * 4. (Future) ThemeProvider - Theme management
 *
 * @example
 * ```tsx
 * // In main.tsx
 * createRoot(document.getElementById('root')!).render(
 *   <StrictMode>
 *     <AppProviders>
 *       <App />
 *     </AppProviders>
 *   </StrictMode>
 * );
 * ```
 */
export function AppProviders({ children }: AppProvidersProps): JSX.Element {
  return (
    <AppBackgroundProvider>
      <QueryProvider>
        <AuthInitializer>
          {children}
        </AuthInitializer>
      </QueryProvider>
    </AppBackgroundProvider>
  );
}

export default AppProviders;
